import type { MarketingFrame, MarketingLayout, MarketingMotion, MarketingViewport, SubsystemSnapshot } from "../../types";
import { sampleWorkflow, WORKFLOW_SCORE } from '../../editorial/workflowScore';
import { translate } from '../../locale';
import { AI_APP_FRAME_CONTRACT, ARTBOARD, BRIDGE_RESPONSE_CONTRACT, BROWSER_FRAME_CONTRACT, DEMO_CHAIN, EXTENSION_SOURCE_CONTRACT, MCP_SOURCE_CONTRACT, type DemoStep } from "./sourceContract";

export type WhereProofPhase = "intro" | "environments" | "describe" | "check" | "refine" | "complete";
export type ExtensionMode = "auto" | "manual";
export type ScreenshotMode = "on" | "auto" | "off";
export interface WhereProofState {
  phase: WhereProofPhase;
  visibleSteps: number;
  panelOpen: boolean;
  extensionMode: ExtensionMode;
  screenshotMode: ScreenshotMode;
  extensionReceiptVisible: boolean;
  mcpReceiptVisible: boolean;
  modeSwitch: number;
  browserEntry: number;
  mcpEntry: number;
  panelProgress: number;
  fabPress: number;
}
export interface WhereProofSnapshot extends SubsystemSnapshot, WhereProofState { state: "managed"; }
export interface WhereProofMarkup { extension: string; mcp: string; }
type TurnStage = "hidden" | "typing" | "send" | "working" | "streaming" | "answered" | "run" | "bridge-compose" | "bridge-send" | "complete";

// Scroll selects the chapter; its actions use a readable, uninterrupted clock.
export const WORKFLOW_TURN_TIMING = Object.freeze({
  typingEnd: 1700, sendEnd: 1900, thinkingEnd: 3350,
  leadEnd: 3900, streamEnd: 5550, answeredEnd: 6000,
  runEnd: 6350, bridgeComposeEnd: 7000, complete: 7200,
});

const clamp = (value: number): number => Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
const chapterFor = (progress: number): number => Math.min(4, Math.floor(clamp(progress) * 5));
export function sampleTurnProgress(progress: number): readonly number[] {
  const chapter = chapterFor(progress);
  return DEMO_CHAIN.map((_, index) => chapter > index + 1 ? 1 : chapter === index + 1 ? .5 : 0);
}
export function samplePartProgress(_part: string, progress: number): number {
  return progress <= 0 ? 0 : progress >= 1 ? 1 : .5;
}
export function sampleWhereProofState(progress: number, motion: MarketingMotion): WhereProofState {
  const reduced = motion === "reduced";
  const chapter = reduced ? 4 : chapterFor(progress);
  return {
    phase: reduced ? "complete" : (["intro", "environments", "describe", "check", "refine"][chapter] as WhereProofPhase),
    visibleSteps: Math.max(0, chapter - 1), panelOpen: !reduced && chapter === 1,
    extensionMode: chapter >= 1 ? "auto" : "manual", screenshotMode: "auto",
    extensionReceiptVisible: chapter >= 2, mcpReceiptVisible: chapter >= 2,
    modeSwitch: chapter >= 1 ? 1 : 0, browserEntry: chapter >= 1 ? 1 : 0,
    mcpEntry: chapter >= 1 ? 1 : 0, panelProgress: !reduced && chapter === 1 ? 1 : 0, fabPress: 0,
  };
}
const escapeHtml = (value: string): string => value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("'", "&#39;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
const ICONS = {
  back: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3L5 8l5 5"/></svg>',
  forward: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3l5 5-5 5"/></svg>',
  reload: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M13 8a5 5 0 1 1-1.6-3.6M13 2.5V5.5H10"/></svg>',
  lock: '<svg viewBox="0 0 12 12" aria-hidden="true"><rect x="2.5" y="5" width="7" height="5" rx="1.2"/><path d="M4.2 5V3.6a1.8 1.8 0 0 1 3.6 0V5"/></svg>',
  puzzle: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6.4 2.6h3.2v1.6a1.2 1.2 0 1 0 2.4 0V2.6h1.4v3.2h-1.6a1.2 1.2 0 1 0 0 2.4h1.6v3.2h-3.2v-1.6a1.2 1.2 0 1 0-2.4 0v1.6H4.6V8.2H3a1.2 1.2 0 1 0 0-2.4h1.6V2.6h1.8Z"/></svg>',
  globe: '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.5"/><path d="M2.8 8h10.4M8 2.5c1.5 1.5 2.2 3.3 2.2 5.5S9.5 12 8 13.5C6.5 12 5.8 10.2 5.8 8S6.5 4 8 2.5Z"/></svg>',
  close: '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 2L10 10M10 2L2 10"/></svg>',
  extensionClose: '<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 2L10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M10 2L2 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  plus: '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M7 2.5v9M2.5 7h9"/></svg>',
  send: '<svg class="composer-send-symbol" viewBox="0 0 52 52" fill="none" aria-hidden="true" focusable="false"><circle class="composer-send-symbol__ring" cx="26" cy="26" r="25"/><path class="composer-send-symbol__arrow" d="M26 34V18M18 26l8-8 8 8"/></svg>',
  minimize: '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h8"/></svg>',
  maximize: '<svg viewBox="0 0 12 12" aria-hidden="true"><rect x="2.5" y="2.5" width="7" height="7" rx="1"/></svg>',
  cursor: '<svg viewBox="0 0 20 24" aria-hidden="true"><path d="M2 1.5v17l4.7-4.2 3 7.1 3-1.3-3-7h6.4L2 1.5Z"/></svg>',
} as const;
const highlightPython = (code: string): string => code.split("\n").map(raw => {
  let line = escapeHtml(raw);
  line = line.replace(/(&quot;.*?&quot;|&#39;.*?&#39;)/g, '<span class="where-code__string">$1</span>');
  line = line.replace(/\b(import|from|as)\b/g, '<span class="where-code__keyword">$1</span>');
  line = line.replace(/\b(bpy|scene|base|key_light)\b/g, '<span class="where-code__name">$1</span>');
  return `<span class="where-code__line">${line}</span>`;
}).join("");
const bridgeText = (step: DemoStep): string => [
  BRIDGE_RESPONSE_CONTRACT.heading,
  `${BRIDGE_RESPONSE_CONTRACT.operationLabel} ${step.operationName}`,
  `${BRIDGE_RESPONSE_CONTRACT.requestLabel} ${step.requestId}`,
  `${BRIDGE_RESPONSE_CONTRACT.statusLabel} ${BRIDGE_RESPONSE_CONTRACT.status}`,
].join("\n");
const browserTurn = (step: DemoStep, index: number): string => `
  <article class="where-chat__turn" data-chat-turn="${index + 1}" data-turn-state="hidden">
    <div class="where-message where-message--user" data-turn-part="prompt">${escapeHtml(step.prompt)}</div>
    <div class="where-ai-app__thinking" data-turn-part="working" aria-label="AI готовит ответ"><i></i><i></i><i></i></div>
    <div class="where-message where-message--assistant" data-turn-part="answer">
      <p data-answer-lead>${escapeHtml(step.lead)}</p>
      <figure class="where-chat__code" data-code-block><figcaption>${BROWSER_FRAME_CONTRACT.codeLanguage}</figcaption><pre><code data-answer-code>${highlightPython(step.code)}</code></pre></figure>
      <button class="where-chat__run" data-extension-run type="button" hidden>${EXTENSION_SOURCE_CONTRACT.runSingleLabel}</button>
    </div>
    <div class="where-message where-message--user where-message--bridge" data-extension-chat-result data-turn-part="bridge">${escapeHtml(bridgeText(step))}</div>
  </article>`;
const appTurn = (step: DemoStep, index: number, logo: string): string => `
  <article class="where-ai-app__turn" data-chat-turn="${index + 1}" data-turn-state="hidden">
    <div class="where-message where-message--user" data-turn-part="prompt">${escapeHtml(step.prompt)}</div>
    <div class="where-ai-app__thinking" data-turn-part="working"><i></i><i></i><i></i><span>${escapeHtml(step.lead)}</span></div>
    <div class="where-message where-message--assistant where-ai-app__answer" data-turn-part="answer">
      <span class="where-tool-call" data-mcp-tool="${MCP_SOURCE_CONTRACT.toolName}" data-mcp-receipt><img src="${logo}" alt=""/><span>${AI_APP_FRAME_CONTRACT.toolUsed}</span></span>
      <p data-answer-reply>${escapeHtml(step.reply)}</p>
    </div>
  </article>`;
const extensionControl = (kind: "mode" | "screenshot", value: ExtensionMode | ScreenshotMode, label: string, description: string): string =>
  `<button class="where-extension__choice" type="button" data-extension-${kind}-control="${value}" title="${escapeHtml(description)}">${label}</button>`;
const extensionPanel = (logo: string): string => `
  <div class="where-extension-backdrop" aria-hidden="true">
    <section class="where-extension" data-extension-surface="${EXTENSION_SOURCE_CONTRACT.surface}" aria-label="Blender Bridge Extension ${EXTENSION_SOURCE_CONTRACT.sourceRelease}">
      <header class="where-extension__header"><div class="where-extension__title"><img src="${logo}" alt=""/><strong>Blender Bridge</strong></div><button class="where-extension__close" data-extension-close type="button" aria-label="Close">${ICONS.extensionClose}</button></header>
      <div class="where-extension__body">
        <div class="where-extension__left">
          <section class="where-extension__field where-extension__readiness-section"><span class="where-extension__label">${EXTENSION_SOURCE_CONTRACT.readinessLabel}</span><span class="where-extension__readiness" data-tone="ready"><i aria-hidden="true"></i>${EXTENSION_SOURCE_CONTRACT.readinessValue}</span></section>
          <i class="where-extension__separator" aria-hidden="true"></i>
          <section class="where-extension__field"><span class="where-extension__label">${EXTENSION_SOURCE_CONTRACT.mode.label}</span><span class="where-extension__choices">${extensionControl("mode", "auto", EXTENSION_SOURCE_CONTRACT.mode.controls[0], EXTENSION_SOURCE_CONTRACT.mode.descriptions.auto)}${extensionControl("mode", "manual", EXTENSION_SOURCE_CONTRACT.mode.controls[1], EXTENSION_SOURCE_CONTRACT.mode.descriptions.manual)}</span><span class="where-extension__description" data-extension-mode-description>${EXTENSION_SOURCE_CONTRACT.mode.descriptions.manual}</span></section>
          <i class="where-extension__separator" aria-hidden="true"></i>
          <section class="where-extension__field"><span class="where-extension__label">${EXTENSION_SOURCE_CONTRACT.screenshot.label}</span><span class="where-extension__choices">${extensionControl("screenshot", "on", EXTENSION_SOURCE_CONTRACT.screenshot.controls[0], EXTENSION_SOURCE_CONTRACT.screenshot.descriptions.on)}${extensionControl("screenshot", "auto", EXTENSION_SOURCE_CONTRACT.screenshot.controls[1], EXTENSION_SOURCE_CONTRACT.screenshot.descriptions.auto)}${extensionControl("screenshot", "off", EXTENSION_SOURCE_CONTRACT.screenshot.controls[2], EXTENSION_SOURCE_CONTRACT.screenshot.descriptions.off)}</span><span class="where-extension__description">${EXTENSION_SOURCE_CONTRACT.screenshot.descriptions.auto}</span></section>
          <i class="where-extension__separator" aria-hidden="true"></i>
          <section class="where-extension__field"><span class="where-extension__label">${EXTENSION_SOURCE_CONTRACT.siteWork.label}</span><span class="where-extension__site-work">${EXTENSION_SOURCE_CONTRACT.siteWork.controls.map(label => `<button type="button">${label}</button>`).join("")}</span></section>
        </div>
        <div class="where-extension__right"><div class="where-extension__actions">${EXTENSION_SOURCE_CONTRACT.actions.map(label => `<button type="button">${label}</button>`).join("")}</div><div class="where-extension__history-block"><span class="where-extension__history-label">${EXTENSION_SOURCE_CONTRACT.historyLabel}</span><span class="where-extension__history-empty">${EXTENSION_SOURCE_CONTRACT.emptyHistory}</span></div></div>
      </div>
    </section>
    <span class="where-extension__pointer" aria-hidden="true">${ICONS.cursor}</span>
  </div>`;
const composer = (placeholder: string, scope: "browser" | "app"): string => `
  <div class="where-composer where-composer--${scope}" data-composer="${scope}"><span class="where-composer__draft" data-composer-draft></span><span class="where-composer__placeholder">${placeholder}</span><i class="where-composer__caret" aria-hidden="true"></i><button class="where-composer__send" data-composer-send type="button" aria-label="Отправить">${ICONS.send}</button></div>`;
export function renderWhereProofMarkup(extensionLogoUrl: string): WhereProofMarkup {
  const logo = escapeHtml(extensionLogoUrl);
  return {
    extension: `<div class="where-viewport"><div class="where-artboard" style="--artboard-w:${ARTBOARD.width}px;--artboard-h:${ARTBOARD.height}px"><div class="where-browser" data-neutral-browser aria-label="Браузер с Blender Bridge Extension">
      <div class="where-browser__titlebar" aria-hidden="true"><span class="where-browser__tab">${ICONS.globe}<strong>${BROWSER_FRAME_CONTRACT.tabTitle}</strong>${ICONS.close}</span><span class="where-browser__tab-new">${ICONS.plus}</span><span class="where-browser__window-controls">${ICONS.minimize}${ICONS.maximize}${ICONS.close}</span></div>
      <div class="where-browser__toolbar" aria-hidden="true"><span class="where-browser__nav">${ICONS.back}${ICONS.forward}${ICONS.reload}</span><span class="where-browser__omnibox">${ICONS.lock}<i></i><i></i><i></i></span><span class="where-browser__toolbar-end">${ICONS.puzzle}<img src="${logo}" alt=""/></span></div>
      <div class="where-browser__page"><div class="where-thread" data-chat-thread><div class="where-thread__inner">${DEMO_CHAIN.map(browserTurn).join("")}</div></div>${composer("Сообщение для AI", "browser")}<button class="where-launcher" data-extension-launcher type="button" aria-label="Blender Bridge"><span><img src="${logo}" alt=""/></span></button>${extensionPanel(logo)}</div>
    </div></div></div>`,
    mcp: `<div class="where-viewport"><div class="where-artboard" style="--artboard-w:${ARTBOARD.width}px;--artboard-h:${ARTBOARD.height}px"><div class="where-ai-app" data-neutral-ai-app aria-label="AI приложение с Blender Bridge MCP">
      <div class="where-ai-app__titlebar" aria-hidden="true"><span></span><strong>${AI_APP_FRAME_CONTRACT.windowTitle}</strong><span class="where-ai-app__window-controls">${ICONS.minimize}${ICONS.maximize}${ICONS.close}</span></div>
      <main class="where-ai-app__conversation"><div class="where-thread" data-chat-thread><div class="where-thread__inner">${DEMO_CHAIN.map((step, index) => appTurn(step, index, logo)).join("")}</div></div>${composer("Спроси что-нибудь", "app")}</main>
    </div></div></div>`,
  };
}

interface SlotState { readonly node: HTMLElement; readonly html: string; readonly hadFallback: boolean; }
const stageAt = (elapsed: number): TurnStage => {
  const timing = WORKFLOW_TURN_TIMING;
  if (elapsed < timing.typingEnd) return "typing";
  if (elapsed < timing.sendEnd) return "send";
  if (elapsed < timing.thinkingEnd) return "working";
  if (elapsed < timing.streamEnd) return "streaming";
  if (elapsed < timing.answeredEnd) return "answered";
  if (elapsed < timing.runEnd) return "run";
  if (elapsed < timing.bridgeComposeEnd) return "bridge-compose";
  if (elapsed < timing.complete) return "bridge-send";
  return "complete";
};
const typedText = (value: string, elapsed: number): string => value.slice(0, Math.round(value.length * clamp((elapsed - 140) / 1480)));

export class WhereProofController {
  private readonly extensionSlot: SlotState;
  private readonly mcpSlot: SlotState;
  private layout: MarketingLayout | null = null;
  private current = sampleWhereProofState(0, "full");
  private destroyed = false;
  private chapter = -1;
  private chapterStartedAt = 0;
  private scrollState = "";
  private extensionPointerStage = "";
  private extensionApproachTarget: { x: number; y: number } | null = null;

  constructor(root: ParentNode, extensionLogoUrl = "/assets/img/logo.png") {
    const extension = root.querySelector<HTMLElement>('[data-proof-slot="extension"]');
    const mcp = root.querySelector<HTMLElement>('[data-proof-slot="mcp"]');
    if (!extension || !mcp) throw new Error("WhereProofController requires extension and mcp proof slots.");
    this.extensionSlot = { node: extension, html: extension.innerHTML, hadFallback: extension.hasAttribute("data-proof-fallback") };
    this.mcpSlot = { node: mcp, html: mcp.innerHTML, hadFallback: mcp.hasAttribute("data-proof-fallback") };
    const markup = renderWhereProofMarkup(extensionLogoUrl);
    extension.innerHTML = markup.extension;
    mcp.innerHTML = markup.mcp;
    for (const { node } of [this.extensionSlot, this.mcpSlot]) {
      node.removeAttribute("data-proof-fallback");
      node.dataset.proofEnhanced = "where";
      node.dataset.proofEntry = "hidden";
    }
    this.fitArtboards();
  }
  private sequenceProgress(frame: MarketingFrame): number {
    if (!this.layout) return clamp(frame.sections.workflow.progress);
    const span = this.layout.sections.workflow;
    return clamp((frame.travelPx - span.startPx) / Math.max(1, span.endPx - span.startPx));
  }
  render(frame: MarketingFrame, now = 0): WhereProofSnapshot {
    if (this.destroyed) throw new Error("WhereProofController has been destroyed.");
    const progress = this.sequenceProgress(frame);
    const cinema=this.extensionSlot.node.ownerDocument?.defaultView?.__BB_CINEMA__;
    const directed=cinema?.active&&cinema.chapter.id==='workflow';
    const shot=directed?sampleWorkflow(cinema.state.elapsed):null;
    const chapter = frame.motion === "reduced" ? 4 : shot?shot.index+1:chapterFor(progress);
    if (chapter !== this.chapter) { this.chapter = chapter; this.chapterStartedAt = now; }
    const elapsed = frame.motion === "reduced" ? 10000 : shot?shot.proofElapsed:Math.max(0, now - this.chapterStartedAt);
    this.current = sampleWhereProofState(progress, frame.motion);
    this.applyChapter(chapter, elapsed, frame.motion);
    return { state: "managed", ...this.current };
  }
  resize(viewport: MarketingViewport, layout?: MarketingLayout): void {
    if (this.destroyed) return;
    if (layout) this.layout = layout;
    for (const { node } of [this.extensionSlot, this.mcpSlot]) node.dataset.proofViewport = viewport;
    this.fitArtboards();
  }
  fitArtboards(): void {
    if (this.destroyed) return;
    this.extensionPointerStage = "";
    this.extensionApproachTarget = null;
    for (const { node } of [this.extensionSlot, this.mcpSlot]) {
      const viewport = node.querySelector<HTMLElement>(".where-viewport");
      const artboard = node.querySelector<HTMLElement>(".where-artboard");
      const rect = viewport?.getBoundingClientRect();
      if (rect && artboard && rect.width > 0 && rect.height > 0) artboard.style.setProperty("--artboard-scale", Math.min(rect.width / ARTBOARD.width, rect.height / ARTBOARD.height).toFixed(5));
    }
  }
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const slot of [this.extensionSlot, this.mcpSlot]) {
      slot.node.innerHTML = slot.html;
      for (const key of ["proofEnhanced", "proofViewport", "proofEntry", "proofPhase", "extensionStage", "extensionPanel"]) delete slot.node.dataset[key];
      slot.node.toggleAttribute("data-proof-fallback", slot.hadFallback);
    }
  }
  private applyChapter(chapter: number, elapsed: number, motion: MarketingMotion): void {
    const visible = chapter >= 1 || motion === "reduced";
    this.extensionSlot.node.dataset.proofEntry = visible ? "visible" : "hidden";
    this.mcpSlot.node.dataset.proofEntry = visible ? "visible" : "hidden";
    for (const { node } of [this.extensionSlot, this.mcpSlot]) node.dataset.proofPhase = this.current.phase;
    this.applyExtensionSetup(chapter, elapsed, motion);
    const activeTurn = Math.max(0, Math.min(DEMO_CHAIN.length - 1, chapter - 2));
    const activeStage = chapter >= 2 && elapsed>=0 ? stageAt(elapsed) : "hidden";
    DEMO_CHAIN.forEach((step, index) => {
      const stage: TurnStage = chapter < 2 || index > activeTurn ? "hidden" : index < activeTurn || motion === "reduced" ? "complete" : activeStage;
      for (const { node } of [this.extensionSlot, this.mcpSlot]) {
        const turn = node.querySelector<HTMLElement>(`[data-chat-turn="${index + 1}"]`);
        if (turn) {
          turn.dataset.turnState = stage;
          this.applyAnswer(turn, step, stage, elapsed);
        }
      }
    });
    const composerStage = motion === "reduced" ? "complete" : activeStage;
    this.applyComposer(this.extensionSlot.node, DEMO_CHAIN[activeTurn]!, composerStage, elapsed, true);
    this.applyComposer(this.mcpSlot.node, DEMO_CHAIN[activeTurn]!, composerStage, elapsed, false);
    const scrollState = `${chapter}:${activeStage}:${activeStage === "streaming" ? Math.floor(elapsed / 90) : ""}`;
    if (scrollState !== this.scrollState) {
      this.scrollState = scrollState;
      for (const { node } of [this.extensionSlot, this.mcpSlot]) {
        const thread = node.querySelector<HTMLElement>("[data-chat-thread]");
        if (thread) thread.scrollTop = thread.scrollHeight;
      }
    }
  }
  private applyAnswer(turn: HTMLElement, step: DemoStep, stage: TurnStage, elapsed: number): void {
    step={...step,lead:translate(step.lead),reply:translate(step.reply)};
    const timing = WORKFLOW_TURN_TIMING;
    const streaming = stage === "streaming";
    const ready = ["answered", "run", "bridge-compose", "bridge-send", "complete"].includes(stage);
    const lead = turn.querySelector<HTMLElement>("[data-answer-lead]");
    const code = turn.querySelector<HTMLElement>("[data-answer-code]");
    const reply = turn.querySelector<HTMLElement>("[data-answer-reply]");
    const leadText = ready ? step.lead : streaming ? step.lead.slice(0, Math.round(step.lead.length * clamp((elapsed - timing.thinkingEnd) / (timing.leadEnd - timing.thinkingEnd)))) : "";
    if (lead && lead.textContent !== leadText) lead.textContent = leadText;
    const codeLength = ready ? step.code.length : streaming ? Math.round(step.code.length * clamp((elapsed - timing.leadEnd) / (timing.streamEnd - timing.leadEnd))) : 0;
    if (code && code.dataset.visibleCharacters !== String(codeLength)) {
      code.innerHTML = highlightPython(step.code.slice(0, codeLength));
      code.dataset.visibleCharacters = String(codeLength);
    }
    turn.querySelector<HTMLElement>("[data-code-block]")?.toggleAttribute("hidden", !ready && codeLength === 0);
    turn.querySelector<HTMLElement>("[data-extension-run]")?.toggleAttribute("hidden", !ready);
    // Tool execution precedes the final response; only that final response stays.
    const replyText = ready ? step.reply : streaming ? step.reply.slice(0, Math.round(step.reply.length * clamp((elapsed - timing.thinkingEnd - 550) / 950))) : "";
    if (reply && reply.textContent !== replyText) reply.textContent = replyText;
  }
  private applyComposer(slot: HTMLElement, step: DemoStep, stage: TurnStage, elapsed: number, browser: boolean): void {
    step={...step,prompt:translate(step.prompt)};
    const composerNode = slot.querySelector<HTMLElement>("[data-composer]");
    const draft = composerNode?.querySelector<HTMLElement>("[data-composer-draft]");
    const send = composerNode?.querySelector<HTMLElement>("[data-composer-send]");
    if (draft) {
      draft.textContent = stage === "typing" ? typedText(step.prompt, elapsed) : stage === "send" ? step.prompt
        : browser && (stage === "bridge-compose" || stage === "bridge-send") ? translate(bridgeText(step)) : "";
    }
    send?.toggleAttribute("data-pressed", stage === "send" || (browser && stage === "bridge-send"));
    composerNode?.toggleAttribute("data-has-draft", Boolean(draft?.textContent));
    composerNode?.toggleAttribute("data-bridge-draft", browser && (stage === "bridge-compose" || stage === "bridge-send"));
  }
  private applyExtensionSetup(chapter: number, elapsed: number, motion: MarketingMotion): void {
    const slot = this.extensionSlot.node;
    let stage = "closed";
    let mode: ExtensionMode = "manual";
    if (motion === "reduced" || chapter > 1) { stage = "closed-after"; mode = "auto"; }
    else if (chapter === 1) {
      const setupElapsed = elapsed - WORKFLOW_SCORE.extensionSetupDelay;
      if (setupElapsed < 800) stage = "closed";
      else if (setupElapsed < 1000) stage = "fab-press";
      else if (setupElapsed < 1700) stage = "opening";
      else if (setupElapsed < 3000) stage = "manual";
      else if (setupElapsed < 3500) stage = "switching";
      else if (setupElapsed < 4300) stage = "auto";
      else if (setupElapsed < 5600) stage = "close-travel";
      else if (setupElapsed < 5750) stage = "close-hover";
      else if (setupElapsed < 5950) stage = "close-press";
      else stage = "closed-after";
      if (setupElapsed >= 3300) mode = "auto";
    }
    const panelOpen = ["opening", "manual", "switching", "auto", "close-travel", "close-hover", "close-press"].includes(stage);
    slot.dataset.extensionStage = stage;
    slot.dataset.extensionPanel = panelOpen ? "open" : "closed";
    this.current = {
      ...this.current, panelOpen, extensionMode: mode, modeSwitch: mode === "auto" ? 1 : 0,
      panelProgress: panelOpen ? 1 : 0, fabPress: stage === "fab-press" ? 1 : 0,
      browserEntry: slot.dataset.proofEntry === "visible" ? 1 : 0,
      mcpEntry: this.mcpSlot.node.dataset.proofEntry === "visible" ? 1 : 0,
    };
    slot.querySelectorAll<HTMLElement>("[data-extension-mode-control]").forEach(control => control.toggleAttribute("data-selected", control.dataset.extensionModeControl === mode));
    slot.querySelectorAll<HTMLElement>("[data-extension-screenshot-control]").forEach(control => control.toggleAttribute("data-selected", control.dataset.extensionScreenshotControl === "auto"));
    const description = slot.querySelector<HTMLElement>("[data-extension-mode-description]");
    if (description) description.textContent = translate(EXTENSION_SOURCE_CONTRACT.mode.descriptions[mode]);
    const approaching = chapter === 1 && stage === "closed";
    const pointer = slot.querySelector<HTMLElement>(".where-extension__pointer");
    if (approaching) {
      if (stage !== this.extensionPointerStage) this.extensionApproachTarget = null;
      this.extensionPointerStage = stage;
      if (!pointer) return;
      pointer.style.setProperty("transition", "none");
      pointer.style.setProperty("opacity", String(clamp(elapsed / 140)));
      if (elapsed < 0) { this.extensionApproachTarget = null; return; }
      if (!this.extensionApproachTarget) {
        const target = slot.querySelector<HTMLElement>("[data-extension-launcher]")?.getBoundingClientRect();
        const bounds = slot.querySelector<HTMLElement>(".where-extension-backdrop")?.getBoundingClientRect();
        const scale = (bounds?.width ?? 0) / ARTBOARD.width;
        if (target && bounds && scale > 0) this.extensionApproachTarget = {
          x: (target.left + target.width * .56 - bounds.left) / scale,
          y: (target.top + target.height * .54 - bounds.top) / scale,
        };
      }
      if (this.extensionApproachTarget) {
        const t = clamp(elapsed / WORKFLOW_SCORE.extensionApproachDuration), eased = t*t*(3-2*t);
        pointer.style.setProperty("left", `${this.extensionApproachTarget.x - 220 * (1-eased)}px`);
        pointer.style.setProperty("top", `${this.extensionApproachTarget.y - 150 * (1-eased)}px`);
      }
      return;
    }
    if (stage === this.extensionPointerStage) return;
    pointer?.style.removeProperty("transition");
    pointer?.style.removeProperty("opacity");
    this.extensionPointerStage = stage;
    const selector = stage.startsWith("close") ? "[data-extension-close]"
      : stage === "switching" || stage === "auto" ? '[data-extension-mode-control="auto"]'
      : stage === "opening" || stage === "manual" ? '[data-extension-mode-control="manual"]' : "[data-extension-launcher]";
    const target = slot.querySelector<HTMLElement>(selector);
    const backdrop = slot.querySelector<HTMLElement>(".where-extension-backdrop");
    if (target?.getBoundingClientRect && backdrop?.getBoundingClientRect && pointer) {
      const bounds = backdrop.getBoundingClientRect();
      const button = target.getBoundingClientRect();
      const scale = bounds.width / ARTBOARD.width;
      if (scale > 0) {
        const x=(button.left+button.width*.56-bounds.left)/scale,y=(button.top+button.height*.54-bounds.top)/scale;
        const distance=Math.hypot(x-parseFloat(pointer.style.getPropertyValue('left')||String(x)),y-parseFloat(pointer.style.getPropertyValue('top')||String(y)));
        const seconds=Math.max(.65,Math.min(1.3,distance/240));
        pointer.style.setProperty('transition',`left ${seconds}s cubic-bezier(.4,0,.6,1), top ${seconds}s cubic-bezier(.4,0,.6,1), opacity .14s ease, transform .14s ease`);
        pointer.style.setProperty("left", `${(button.left + button.width * .56 - bounds.left) / scale}px`);
        pointer.style.setProperty("top", `${(button.top + button.height * .54 - bounds.top) / scale}px`);
      }
    }
  }
}
